import React, { useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  IconLink,
  IconListDetails,
  IconCloudUpload,
  IconChevronRight,
  IconMessage,
  IconCircleCheckFilled,
  IconPlug,
} from "@tabler/icons-react";
import { IonContent, IonPage, IonSpinner, useIonToast } from "@ionic/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { FormattedMessage, useIntl } from "react-intl";
import { useSelector } from "react-redux";

import { UrlWithStatus, useCrawlingActions, useCrawlingData } from "../../store/crawling";
import { useSockets } from "../../providers/socket";
import { makeSelectUser } from "../../selectors";
import normalizeUrl from "./website/utils";

import AiApi from "../../api/ai";
import SkeletonText from "../SkeletonText";
import SourcesAlert from "./sources/alert";

import styles from "./styles.module.css";

type AiLayoutProps = {
  children: React.ReactNode;
};

const NAV_ITEMS = [
  {
    path: "/ai/website",
    key: "website",
    label: "ai.sources.website",
    icon: <IconLink size={17} />,
  },
  {
    path: "/ai/text",
    key: "text",
    label: "ai.sources.text",
    icon: <IconListDetails size={17} />,
  },
  {
    path: "/ai/files",
    key: "files",
    label: "ai.sources.files",
    icon: <IconCloudUpload size={17} />,
  },
  {
    path: "/ai/providers",
    key: "provider",
    label: "ai.sources.providers",
    icon: <IconPlug size={17} />,
  },
];

const HEADER_DESCRIPTIONS: Record<string, React.ReactNode> = {
  "/ai/website": (
    <>
      <h2 className="fs-5 mb-0">
        <FormattedMessage id="ai.sources.website" />
      </h2>
      <p className="description mb-0">
        <FormattedMessage id="ai.sources.website.desc" />
      </p>
    </>
  ),
  "/ai/text": (
    <>
      <h2 className="fs-5 mb-0">
        <FormattedMessage id="ai.sources.text" />
      </h2>
      <p className="description mb-0">
        <FormattedMessage id="ai.sources.text.desc" />
      </p>
    </>
  ),
  "/ai/files": (
    <>
      <h2 className="fs-5 mb-0">
        <FormattedMessage id="ai.sources.files" />
      </h2>
      <p className="description mb-0">
        <FormattedMessage id="ai.sources.files.desc" />
      </p>
    </>
  ),
  "/ai/providers": (
    <>
      <h2 className="fs-5 mb-0">
        <FormattedMessage id="ai.sources.providers" />
      </h2>
      <p className="description mb-0">
        <FormattedMessage id="ai.sources.providers.desc" />
      </p>
    </>
  ),
  "/ai/customer": (
    <>
      <h2 className="fs-5 mb-0">
        <FormattedMessage id="ai.sources.customerContext" />
      </h2>
      <p className="description mb-0">
        <FormattedMessage id="ai.sources.customerContext.desc" />
      </p>
    </>
  )
};

const LayoutSkeleton = () => (
  <IonPage id="main-content">
    <IonContent>
      <div className="page-wrapper">
        <div className="w-100 h-100">
          <div className="row h-100">
            <div className="col-md-4">
              <div className="flex flex-column w-100 h-100 p-5">
                <SkeletonText rows={16} />
              </div>
            </div>
            <div className="col-md-8">
              <div className="flex flex-column w-100 h-100 p-5">
                <SkeletonText rows={16} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </IonContent>
  </IonPage>
);

const baseLinkClass = "flex flex-column gap-2 no-underline p-3";

export default function AiLayout({ children }: AiLayoutProps) {
  const location = useLocation();
  const path = location.pathname;

  const queryClient = useQueryClient();
  const intl = useIntl();
  const [present] = useIonToast();

  const { fullCrawl, singleCrawl } = useCrawlingData();
  const { setFullCrawl, setSingleCrawl, reset: resetCrawling } = useCrawlingActions();
  const { isScanning } = fullCrawl;

  const { userSocket } = useSockets();

  const { company } = useSelector(makeSelectUser());

  const headerContent = useMemo(
    () => HEADER_DESCRIPTIONS[path] || null,
    [path]
  );
  const isHome = path === "/ai";

  const { data, isLoading: isRequesting } = useQuery({
    queryFn: () => AiApi.getAiStatus(),
    queryKey: ["getAiStatus"],
    refetchOnWindowFocus: false,
    staleTime: 300000,
  });

  const setUrlUpdate = (message: any) => {
      const isSingleCrawl = message.source === "single-crawl";
      const currentArr = isSingleCrawl ? singleCrawl : fullCrawl?.urls || [];
    
      const indexToUpdate = currentArr.findIndex(u => 
        normalizeUrl(u.url) === normalizeUrl(message.url)
      );
      if (indexToUpdate === -1) {
        const updatedArr = [...currentArr, { ...message, active: message.completed }];
        return isSingleCrawl
          ? setSingleCrawl(updatedArr)
          : setFullCrawl({ urls: updatedArr });
      };
    
      const updatedArr = [...currentArr];
      updatedArr[indexToUpdate] = {
        ...updatedArr[indexToUpdate],
        ...message,
        active: message.completed,
      };
  
      if (message.completed) {
        present({
          message: intl.formatMessage({ id: "aiUpdateSucces" }),
          duration: 4000,
          color: "light",
          position: "top",
          cssClass: "success-toast",
        });
      }
    
      return isSingleCrawl
        ? setSingleCrawl(updatedArr)
        : setFullCrawl({ urls: updatedArr });
    }

  React.useEffect(() => {
    if (!userSocket) return;
    const handleCompanyMessage = (event: any) => {
      // Only listen to events from current company;
      if (company?.id !== event?.companyId) return;
          
      if (event.data?.task !== "ai-crawling" && event.data?.task !== "ai-single-crawling") return;

      const handleCompletion = async () => {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ["getAiStatus"] }),
          queryClient.invalidateQueries({ queryKey: ["getWebsite"] })
        ]);
      };

      if (event.data?.task === "ai-crawling") {
        const {
          message: {
            urlsWithStatus = [],
            isScanning = false,
            isOptimizing = false,
            failed = false,
            completed = false,
          } = {},
        } = event.data;
    
        if (!urlsWithStatus) return;
    
        if (failed) {
          present({
            message: intl.formatMessage({ id: "aiCrawlingFailed" }),
            duration: 5800,
            color: "light",
            position: "top",
            cssClass: "error-toast",
          });
          return resetCrawling();
        }
    
        if (completed) {
          handleCompletion();
        }
    
        setFullCrawl({
          urls: urlsWithStatus,
          isOptimizing,
          isScanning,
          failed,
        });
      }

      if (event.data?.task === "ai-single-crawling") {
        const message: UrlWithStatus = event.data?.message;
        if (!message) return;

        if (message.completed && fullCrawl?.urls?.length === 0 && singleCrawl?.length === 1) {
          handleCompletion();
        }

        if (message.failed) {
          present({
            message: intl.formatMessage({ id: "aiCrawlingFailed" }),
            duration: 5800,
            color: "light",
            position: "top",
            cssClass: "error-toast",
          });
          resetCrawling()
          return handleCompletion();
        }

        setUrlUpdate(message);
      }
    };
        
    userSocket.on("company-message", handleCompanyMessage);
    return () => {
      userSocket.off("company-message");
    };
  }, [userSocket, fullCrawl, singleCrawl]);

  if (isRequesting) {
    return <LayoutSkeleton />;
  }

  const aiData: Record<string, any> | null = data?.status || null;

  return (
    <IonPage id="main-content">
      <IonContent>
        <div className="page-wrapper">
          <div className="w-100 h-100">
            <div className="row h-100">
              <div className={`col-md-4 ${styles.navContainer}`}>
                <div className="flex flex-column align-items-center gap-4 w-100 h-100 px-5 py-4">
                  <div className="flex flex-column gap-2 w-100">
                    <h2 className="fs-5 mb-0 text-start w-100 flex flex-column gap-1">
                      <FormattedMessage id="ai.sources" />
                      <span className="description fs-2 fw-normal">
                        <FormattedMessage id="ai.sources.desc" />
                      </span>
                    </h2>
                    <nav className="flex flex-column gap-3 border-0 w-100 bg-color-0">
                      {NAV_ITEMS.map(({ path: linkPath, label, key, icon }) => (
                        <Link
                          key={linkPath}
                          to={linkPath}
                          className={`${baseLinkClass} ${styles.link} ${
                            path === linkPath ? styles.activeLink : ""
                          }`}
                        >
                          <div className="flex align-items-center justify-content-between gap-2 w-100">
                            <div className="flex align-items-center gap-2">
                              {icon}
                              <FormattedMessage id={label} />
                              {key === "website" && isScanning ? (
                                <IonSpinner
                                  name="circular"
                                  style={{
                                    color: "var(--ion-color-step-500)",
                                    width: "17px",
                                    height: "17px"
                                  }}
                                />
                              ) : aiData?.[key] ? (
                                <IconCircleCheckFilled
                                  style={{
                                    color: "var(--visito-color-active)",
                                  }}
                                  size={18}
                                />
                              ) : (
                                <span className={styles.statusBadge}>
                                  <FormattedMessage id="common.setUp" />
                                </span>
                              )}
                            </div>
                            <IconChevronRight size={18} />
                          </div>
                        </Link>
                      ))}
                    </nav>
                  </div>

                  <div className="flex flex-column gap-2 w-100">
                    <h2 className="fs-5 mb-0 text-start w-100 flex flex-column gap-1">
                      <FormattedMessage id="ai.playground" />
                      <span className="description fs-2 fw-normal">
                        <FormattedMessage id="ai.playground.desc" />
                      </span>
                    </h2>
                    <Link
                      to="/ai"
                      className={`${baseLinkClass} ${styles.link} ${
                        isHome ? styles.activeLink : ""
                      }`}
                    >
                      <div className="flex align-items-center justify-content-between gap-2 w-100">
                        <div className="flex align-items-center gap-2">
                          <IconMessage size={18} />
                          <FormattedMessage id="ai.playground.test" />
                        </div>
                        <IconChevronRight size={18} />
                      </div>
                    </Link>
                  </div>

                  {/* Just commenting in case we want to return to this layout
                  <div className="flex flex-column gap-2 w-100">
                    <h2 className="fs-5 mb-0 text-start w-100 flex flex-column gap-1">
                      <FormattedMessage id="ai.settings" />
                      <span className="description fs-2 fw-normal">
                        <FormattedMessage id="ai.settings.desc" />
                      </span>
                    </h2>
                    <Link
                      to="/ai/settings"
                      className={`${baseLinkClass} ${styles.link} ${
                        path.includes("/ai/settings") ? styles.activeLink : ""
                      }`}
                    >
                      <div className="flex align-items-center justify-content-between gap-2 w-100">
                        <div className="flex align-items-center gap-2">
                          <IconSettings size={18} />
                          <FormattedMessage id="ai.settings.cta" />
                        </div>
                        <IconChevronRight size={18} />
                      </div>
                    </Link>
                  </div>
                  */}

                </div>
              </div>

              <div className={`col-md-8 pl-0 ${styles.mainContent}`}>
                {!aiData?.trained && isHome && <SourcesAlert />}
                <div
                  className={`w-full flex flex-column gap-3 px-5 py-4 h-100 ${
                    isHome ? "" : "default-bg"
                  }`}
                >
                  {headerContent && (
                    <div className="flex flex-column gap-1">{headerContent}</div>
                  )}
                  {children}
                </div>
              </div>
            </div>
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
}
